import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, ShieldCheck, AlertTriangle, Loader2, Mail, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/staff/list");
      setStaff(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="staff-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Henkilökunta</p>
          <h1 className="text-3xl font-display gradient-text-luxe">Käyttäjien hallinta</h1>
          <p className="text-white/60 text-sm mt-1">Henkilökuntasi käyttäjät, heidän moduulinsa ja 2FA-tila.</p>
        </div>
        <Link to="/admin/users">
          <Button className="btn-luxe inline-flex items-center gap-2">
            <ExternalLink className="w-4 h-4" /> Avaa täysi käyttäjähallinta
          </Button>
        </Link>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={<Users className="w-4 h-4" />} label="Henkilökuntaa" value={staff.length} c="amber" />
        <Stat icon={<ShieldCheck className="w-4 h-4" />} label="2FA käytössä" value={staff.filter(s => s.twofa_enabled).length} c="emerald" />
        <Stat icon={<AlertTriangle className="w-4 h-4" />} label="2FA puuttuu" value={staff.filter(s => !s.twofa_enabled).length} c="red" />
        <Stat icon={<Users className="w-4 h-4" />} label="Aktiivisia" value={staff.filter(s => s.is_active).length} c="blue" />
      </div>

      {loading && <Loader2 className="w-5 h-5 animate-spin text-white/40" />}

      <div className="glass-card p-6">
        <h2 className="text-base font-display text-white mb-4 relative z-10">Lista</h2>
        <div className="space-y-2 relative z-10">
          {staff.length === 0 && !loading && <p className="text-white/40 text-sm">Et ole vielä luonut henkilökuntakäyttäjiä. Klikkaa &quot;Avaa täysi käyttäjähallinta&quot;.</p>}
          {staff.map((s) => (
            <div key={s.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`staff-${s.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                  {(s.full_name || s.email).slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm text-white font-medium">{s.full_name}</p>
                  <p className="text-xs text-white/50 flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="text-[10px] bg-amber-500/15 text-amber-200 border-0">{(s.modules || []).length} moduulia</Badge>
                {s.twofa_enabled
                  ? <Badge className="text-[10px] bg-emerald-500/20 text-emerald-200 border-0">2FA ✓</Badge>
                  : <Badge className="text-[10px] bg-red-500/20 text-red-200 border-0">2FA ✗</Badge>}
                {s.is_active
                  ? <Badge className="text-[10px] bg-emerald-500/20 text-emerald-200 border-0">Aktiivinen</Badge>
                  : <Badge className="text-[10px] bg-zinc-500/20 text-zinc-200 border-0">Suljettu</Badge>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, c }) {
  const colors = { amber: "text-amber-300", emerald: "text-emerald-300", red: "text-red-300", blue: "text-blue-300" };
  return (
    <div className="stat-block">
      <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center ${colors[c]} mb-2`}>{icon}</div>
      <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
      <p className={`text-2xl font-display font-bold ${colors[c]}`}>{value}</p>
    </div>
  );
}
